import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  Pressable,
  TextInput,
  ActivityIndicator,
  Platform,
  KeyboardAvoidingView,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { NativeStackScreenProps } from '@react-navigation/native-stack';
import { useDispatch, useSelector } from 'react-redux';

import type { RootStackParamList } from '../navigation/stackTypes';
import { RootState } from '../store';
import { UserSessionAttributes } from '../models/user-session-model';
import {
  UserAttributes,
  createUser,
} from '../models/login-register-model';
import { HeaderWithMenu } from '../components/menu';
import LoadingButton from '../components/LoadingButton';
import DownloadAppGate from '../components/DownloadAppGate';
// @ts-ignore - Platform-specific file (.native/.web)
import TurnstileChallenge from '../components/captcha/TurnstileChallenge';

import { isSuccessful } from '../services/api-requests';
import { notificationService } from '../services/notification-service';
import { getUserInfo } from '../services/user-service';
import {
  requestEmailOtp,
  confirmEmailOtp,
  selfPromoteToHost,
} from '../services/host-onboarding-service';
import { userActions } from '../store/slices/user-slice';
import { setSession } from '../store/actions/user-session';
import { validateUserToken } from '../store/actions/user-actions';
import { AppDispatch } from '../store';
import RoleService from '../services/role-service';

type Props = NativeStackScreenProps<RootStackParamList, 'BecomeHost'>;

type WizardStep = 'intro' | 'captcha' | 'otp' | 'promoting' | 'done';

const OAUTH_SERVICES = new Set(['google', 'apple']);
const OTP_LENGTH = 6;
const OTP_RESEND_SECONDS = 30;

const formatPromotionMessage = (raw?: string): string => {
  switch (raw) {
    case 'email_not_verified':
      return 'Please verify your email with the 6-digit code we send you.';
    case 'captcha_failed':
    case 'invalid_captcha':
      return 'Human verification failed. Please try again.';
    case 'rate_limited':
      return 'Too many attempts. Please wait a few minutes and try again.';
    default:
      return raw
        ? raw.replace(/_/g, ' ')
        : 'Could not enable hosting. Please try again.';
  }
};

const BecomeHost: React.FC<Props> = ({ navigation }) => {
  const dispatch = useDispatch<AppDispatch>();
  const session: UserSessionAttributes | undefined = useSelector(
    (state: RootState) => state.user?.session
  );
  const user: UserAttributes | undefined = useSelector(
    (state: RootState) => state.user?.userObj
  );

  const isWeb = Platform.OS === 'web';
  const sessionUserId = session?.id;

  const userMatchesSession = useMemo(
    () =>
      !!sessionUserId && !!user?.uuid && user.uuid === sessionUserId,
    [sessionUserId, user?.uuid]
  );

  const [userInfoReady, setUserInfoReady] = useState(false);

  const isOAuthAccount = useMemo(() => {
    if (!userInfoReady || !userMatchesSession) return false;
    const svc = (user?.service || '').toLowerCase();
    return OAUTH_SERVICES.has(svc);
  }, [userInfoReady, userMatchesSession, user?.service]);

  const emailAlreadyVerified =
    userInfoReady && userMatchesSession && user?.email_verified === true;
  const skipOtp = isOAuthAccount || emailAlreadyVerified;

  const [step, setStep] = useState<WizardStep>('intro');
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [captchaError, setCaptchaError] = useState<string | null>(null);

  const [otpCode, setOtpCode] = useState('');
  const [otpRequested, setOtpRequested] = useState(false);
  const [otpRequesting, setOtpRequesting] = useState(false);
  const [otpConfirming, setOtpConfirming] = useState(false);
  const [otpSendError, setOtpSendError] = useState<string | null>(null);
  const [resendCountdown, setResendCountdown] = useState(0);
  const otpAutoSendAttemptedRef = useRef(false);

  const [promotionError, setPromotionError] = useState<string | null>(null);

  const isUnauthenticated = !session || RoleService.isGuest(session);
  const isAlreadyHost = RoleService.hasAnyRole(session, [
    'host',
    'admin',
    'partner',
  ]);

  useEffect(() => {
    if (resendCountdown <= 0) return;
    const timer = setTimeout(
      () => setResendCountdown((s) => Math.max(0, s - 1)),
      1000
    );
    return () => clearTimeout(timer);
  }, [resendCountdown]);

  useEffect(() => {
    if (isAlreadyHost) {
      navigation.replace('OnboardingStatus');
    }
  }, [isAlreadyHost, navigation]);

  useEffect(() => {
    if (isUnauthenticated) {
      setUserInfoReady(false);
      return;
    }
    setUserInfoReady(false);
    let cancelled = false;
    (async () => {
      const refreshed = await getUserInfo();
      if (cancelled) return;
      const fetchedUser = refreshed.payload?.user;
      if (
        isSuccessful(refreshed.status) &&
        fetchedUser &&
        fetchedUser.uuid === sessionUserId
      ) {
        dispatch(userActions.setUserObj({ userObj: fetchedUser }));
        setUserInfoReady(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [dispatch, isUnauthenticated, sessionUserId]);

  const goToCaptcha = useCallback(() => {
    setPromotionError(null);
    setCaptchaError(null);
    setCaptchaToken(null);
    setStep('captcha');
  }, []);

  const beginEmailVerification = useCallback(() => {
    setPromotionError(null);
    setOtpCode('');
    setOtpRequested(false);
    setOtpSendError(null);
    otpAutoSendAttemptedRef.current = false;
    setStep('otp');
  }, []);

  const promoteWithToken = useCallback(
    async (token: string) => {
      setPromotionError(null);
      setStep('promoting');
      try {
        const resp = await selfPromoteToHost(token);
        if (!isSuccessful(resp.status)) {
          const rawMessage = resp.payload?.message;
          if (rawMessage === 'email_not_verified') {
            dispatch(
              userActions.setUserObj({
                userObj: {
                  ...(user ?? createUser()),
                  email_verified: false,
                },
              })
            );
            notificationService.sendNotification(
              'info',
              'Verify your email to continue becoming a host.'
            );
            beginEmailVerification();
            return;
          }
          const message = formatPromotionMessage(rawMessage);
          setPromotionError(message);
          notificationService.sendNotification('error', message);
          setStep('intro');
          setCaptchaToken(null);
          return;
        }

        // Refresh the session FIRST so role-gated UI (SideMenu, OnboardingStatus
        // task list) immediately reflects the new 'host' role. validateUserToken
        // returns fresh UserSessionAttributes including the updated role array.
        try {
          const validated = await validateUserToken();
          if (isSuccessful(validated.status) && validated.payload) {
            await dispatch(setSession(validated.payload));
          }
        } catch (sessionErr) {
          console.warn(
            '[BecomeHost] session refresh after promotion failed',
            sessionErr
          );
        }

        // Then refresh the user object (drives task list, hostingEnabled flag,
        // onboarding_tasks).
        const refreshed = await getUserInfo();
        if (isSuccessful(refreshed.status) && refreshed.payload?.user) {
          dispatch(
            userActions.setUserObj({ userObj: refreshed.payload.user })
          );
        }

        setStep('done');
        notificationService.sendNotification(
          'info',
          'Welcome to the host program! Finish onboarding to start earning.'
        );
        setTimeout(() => {
          navigation.replace('OnboardingStatus');
        }, 600);
      } catch (e) {
        const message = 'Could not enable hosting. Please try again.';
        setPromotionError(message);
        notificationService.sendNotification('error', message);
        setStep('intro');
        setCaptchaToken(null);
      }
    },
    [beginEmailVerification, dispatch, navigation, user]
  );

  const handleTokenReceived = useCallback(
    (token: string) => {
      if (!userInfoReady || !userMatchesSession) {
        notificationService.sendNotification(
          'info',
          'Still loading your account. Please try again in a moment.'
        );
        return;
      }
      setCaptchaToken(token);
      setCaptchaError(null);
      if (skipOtp) {
        promoteWithToken(token);
      } else {
        setStep('otp');
      }
    },
    [promoteWithToken, skipOtp, userInfoReady, userMatchesSession]
  );

  const handleCaptchaError = useCallback((message?: string) => {
    setCaptchaError(
      message
        ? `Verification failed: ${message}`
        : 'Verification failed. Please try again.'
    );
  }, []);

  const handleCaptchaExpire = useCallback(() => {
    setCaptchaToken(null);
    setCaptchaError('Verification expired. Please verify again.');
  }, []);

  const sendOtp = useCallback(async () => {
    if (otpRequesting || resendCountdown > 0) return;
    setOtpRequesting(true);
    setOtpSendError(null);
    try {
      const resp = await requestEmailOtp();
      if (isSuccessful(resp.status)) {
        setOtpRequested(true);
        setResendCountdown(
          resp.payload?.ttlSeconds
            ? Math.min(resp.payload.ttlSeconds, OTP_RESEND_SECONDS)
            : OTP_RESEND_SECONDS
        );
        notificationService.sendNotification(
          'info',
          `We sent a 6-digit code to ${session?.email || 'your email'}.`
        );
      } else if (resp.status === 429) {
        const waitSeconds = resp.payload?.ttlSeconds ?? 60;
        setResendCountdown(waitSeconds);
        const message =
          'Too many code requests. Please wait before trying again.';
        setOtpSendError(message);
        notificationService.sendNotification('error', message);
      } else {
        const message =
          'Unable to send verification code. Use Resend code to try again.';
        setOtpSendError(message);
        notificationService.sendNotification('error', message);
      }
    } catch (e) {
      const message =
        'Unable to send verification code. Use Resend code to try again.';
      setOtpSendError(message);
      notificationService.sendNotification('error', message);
    } finally {
      setOtpRequesting(false);
    }
  }, [otpRequesting, resendCountdown, session?.email]);

  useEffect(() => {
    if (step !== 'otp') {
      otpAutoSendAttemptedRef.current = false;
      return;
    }
    if (otpRequested || otpRequesting || otpAutoSendAttemptedRef.current) {
      return;
    }
    otpAutoSendAttemptedRef.current = true;
    sendOtp();
  }, [step, otpRequested, otpRequesting, sendOtp]);

  const confirmOtp = useCallback(async () => {
    if (otpCode.length !== OTP_LENGTH) return;
    if (!captchaToken) {
      notificationService.sendNotification(
        'error',
        'Verification token missing. Please restart.'
      );
      setStep('captcha');
      return;
    }
    setOtpConfirming(true);
    try {
      const resp = await confirmEmailOtp(otpCode);
      if (!isSuccessful(resp.status) || !resp.payload?.email_verified) {
        notificationService.sendNotification(
          'error',
          resp.payload?.message || 'Incorrect or expired code.'
        );
        return;
      }
      dispatch(
        userActions.setUserObj({
          userObj: {
            ...(user ?? createUser()),
            email_verified: true,
          },
        })
      );
      await promoteWithToken(captchaToken);
    } catch (e) {
      notificationService.sendNotification(
        'error',
        'Unable to verify code. Please try again.'
      );
    } finally {
      setOtpConfirming(false);
    }
  }, [captchaToken, dispatch, otpCode, promoteWithToken, user]);

  if (isWeb) {
    return (
      <View style={styles.container}>
        <SafeAreaView style={styles.safeArea} edges={['top']}>
          <HeaderWithMenu forceHamburger title="Become a Host" insideSafeArea />
          <DownloadAppGate />
        </SafeAreaView>
      </View>
    );
  }

  if (isUnauthenticated) {
    return (
      <View style={styles.container}>
        <SafeAreaView style={styles.safeArea} edges={['top']}>
          <HeaderWithMenu forceHamburger title="Become a Host" insideSafeArea />
          <View style={styles.centeredContent}>
            <Text style={styles.bodyText}>
              Please sign in to become a host.
            </Text>
            <Pressable
              onPress={() => navigation.navigate('WelcomeScreen')}
              style={({ pressed }) => [
                styles.primaryButton,
                pressed && styles.primaryButtonPressed,
              ]}
            >
              <Text style={styles.primaryButtonText}>Sign in</Text>
            </Pressable>
          </View>
        </SafeAreaView>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <SafeAreaView style={styles.safeArea} edges={['top']}>
        <HeaderWithMenu forceHamburger title="Become a Host" insideSafeArea />
        <KeyboardAvoidingView
          style={{ flex: 1 }}
          behavior={Platform.OS === 'ios' ? 'padding' : undefined}
        >
          <ScrollView
            style={styles.content}
            contentContainerStyle={styles.contentContainer}
            keyboardShouldPersistTaps="handled"
          >
            <StepIndicator currentStep={step} skipOtp={skipOtp} />

            {step === 'intro' && (
              <IntroSection
                email={session?.email}
                isOAuthAccount={isOAuthAccount}
                accountReady={userInfoReady && userMatchesSession}
                onContinue={goToCaptcha}
                promotionError={promotionError}
              />
            )}

            {step === 'captcha' && (
              <CaptchaSection
                captchaError={captchaError}
                onVerify={handleTokenReceived}
                onError={handleCaptchaError}
                onExpire={handleCaptchaExpire}
                onBack={() => setStep('intro')}
              />
            )}

            {step === 'otp' && (
              <OtpSection
                email={session?.email}
                otpCode={otpCode}
                setOtpCode={setOtpCode}
                otpRequesting={otpRequesting}
                otpConfirming={otpConfirming}
                otpSendError={otpSendError}
                otpRequested={otpRequested}
                resendCountdown={resendCountdown}
                onResend={sendOtp}
                onConfirm={confirmOtp}
                onBack={() => setStep('captcha')}
              />
            )}

            {step === 'promoting' && (
              <View style={styles.centeredContent}>
                <ActivityIndicator size="large" color="#39BEB7" />
                <Text style={styles.bodyText}>
                  Enabling host capabilities...
                </Text>
              </View>
            )}

            {step === 'done' && (
              <View style={styles.centeredContent}>
                <MaterialCommunityIcons
                  name="check-circle"
                  size={64}
                  color="#3FC67D"
                />
                <Text style={styles.successTitle}>You're a host!</Text>
                <Text style={styles.bodyText}>
                  Taking you to your onboarding checklist...
                </Text>
              </View>
            )}
          </ScrollView>
        </KeyboardAvoidingView>
      </SafeAreaView>
    </View>
  );
};

interface StepIndicatorProps {
  currentStep: WizardStep;
  skipOtp: boolean;
}

const StepIndicator: React.FC<StepIndicatorProps> = ({ currentStep, skipOtp }) => {
  const steps = useMemo(() => {
    const base = [
      { id: 'captcha', label: 'Verify' },
      ...(skipOtp ? [] : [{ id: 'otp', label: 'Email' }]),
      { id: 'promoting', label: 'Activate' },
    ];
    return base;
  }, [skipOtp]);

  const currentIndex = useMemo(() => {
    if (currentStep === 'intro' || currentStep === 'captcha') return 0;
    if (currentStep === 'otp') return skipOtp ? 1 : 1;
    if (currentStep === 'promoting' || currentStep === 'done')
      return steps.length - 1;
    return 0;
  }, [currentStep, skipOtp, steps.length]);

  return (
    <View style={styles.stepIndicator}>
      {steps.map((s, idx) => {
        const active = idx <= currentIndex;
        return (
          <React.Fragment key={s.id}>
            <View style={styles.stepItem}>
              <View
                style={[styles.stepDot, active && styles.stepDotActive]}
              >
                <Text
                  style={[
                    styles.stepDotText,
                    active && styles.stepDotTextActive,
                  ]}
                >
                  {idx + 1}
                </Text>
              </View>
              <Text
                style={[
                  styles.stepLabel,
                  active && styles.stepLabelActive,
                ]}
              >
                {s.label}
              </Text>
            </View>
            {idx < steps.length - 1 && (
              <View
                style={[
                  styles.stepConnector,
                  idx < currentIndex && styles.stepConnectorActive,
                ]}
              />
            )}
          </React.Fragment>
        );
      })}
    </View>
  );
};

interface IntroSectionProps {
  email?: string;
  isOAuthAccount: boolean;
  accountReady: boolean;
  onContinue: () => void;
  promotionError: string | null;
}

const IntroSection: React.FC<IntroSectionProps> = ({
  email,
  isOAuthAccount,
  accountReady,
  onContinue,
  promotionError,
}) => (
  <View style={styles.card}>
    <View style={styles.heroIcon}>
      <MaterialCommunityIcons
        name="ev-station"
        size={48}
        color="#39BEB7"
      />
    </View>
    <Text style={styles.cardTitle}>Earn with your charger</Text>
    <Text style={styles.cardSubtitle}>
      Become a Meander host to start monetizing your EV charger. We'll quickly
      verify you're a real person, then guide you through onboarding.
    </Text>

    <View style={styles.bulletList}>
      <Bullet text="Quick human verification (no image puzzles)" />
      {isOAuthAccount ? (
        <Bullet text={`Signed in with ${email || 'your provider'} \u2014 no email code needed`} />
      ) : (
        <Bullet text={`We'll email a 6-digit code to ${email || 'your inbox'}`} />
      )}
      <Bullet text="Free host plan auto-assigned on success" />
      <Bullet text="Finish Stripe onboarding to go live" />
    </View>

    {promotionError && (
      <View style={styles.errorBanner}>
        <MaterialCommunityIcons
          name="alert-circle"
          size={18}
          color="#FFFFFF"
        />
        <Text style={styles.errorBannerText}>{promotionError}</Text>
      </View>
    )}

    <LoadingButton
      onPress={onContinue}
      disabled={!accountReady}
      isLoading={!accountReady}
      backgroundColor="#39BEB7"
      style={styles.continueButton}
      hapticsOnStart
    >
      {accountReady ? 'Get started' : 'Loading account...'}
    </LoadingButton>
  </View>
);

interface CaptchaSectionProps {
  captchaError: string | null;
  onVerify: (token: string) => void;
  onError: (msg?: string) => void;
  onExpire: () => void;
  onBack: () => void;
}

const CaptchaSection: React.FC<CaptchaSectionProps> = ({
  captchaError,
  onVerify,
  onError,
  onExpire,
  onBack,
}) => (
  <View style={styles.card}>
    <Text style={styles.cardTitle}>Verify you're human</Text>
    <Text style={styles.cardSubtitle}>
      This is a one-time check. The widget below will complete on its own most of
      the time.
    </Text>

    <View style={styles.captchaWrap}>
      <TurnstileChallenge
        onVerify={onVerify}
        onError={onError}
        onExpire={onExpire}
        theme="dark"
      />
    </View>

    {captchaError && (
      <Text style={styles.inlineError}>{captchaError}</Text>
    )}

    <Pressable
      onPress={onBack}
      style={({ pressed }) => [
        styles.linkButton,
        pressed && styles.linkButtonPressed,
      ]}
    >
      <Text style={styles.linkText}>Back</Text>
    </Pressable>
  </View>
);

interface OtpSectionProps {
  email?: string;
  otpCode: string;
  setOtpCode: (v: string) => void;
  otpRequesting: boolean;
  otpConfirming: boolean;
  otpSendError: string | null;
  otpRequested: boolean;
  resendCountdown: number;
  onResend: () => void;
  onConfirm: () => void;
  onBack: () => void;
}

const OtpSection: React.FC<OtpSectionProps> = ({
  email,
  otpCode,
  setOtpCode,
  otpRequesting,
  otpConfirming,
  otpSendError,
  otpRequested,
  resendCountdown,
  onResend,
  onConfirm,
  onBack,
}) => {
  const canResend = resendCountdown <= 0 && !otpRequesting;
  const canSubmit = otpCode.length === OTP_LENGTH && !otpConfirming;
  return (
    <View style={styles.card}>
      <Text style={styles.cardTitle}>Confirm your email</Text>
      <Text style={styles.cardSubtitle}>
        {otpRequested
          ? 'We sent a 6-digit code to '
          : 'Enter the 6-digit code we send to '}
        <Text style={styles.emphasis}>{email || 'your email'}</Text>
        {otpRequested
          ? '. Enter it below to finish verification.'
          : '. If you do not receive it, use Resend code below.'}
      </Text>

      {otpSendError && (
        <View style={styles.errorBanner}>
          <MaterialCommunityIcons
            name="alert-circle"
            size={18}
            color="#FFFFFF"
          />
          <Text style={styles.errorBannerText}>{otpSendError}</Text>
        </View>
      )}

      <TextInput
        style={styles.otpInput}
        value={otpCode}
        onChangeText={(v) =>
          setOtpCode(v.replace(/\D/g, '').slice(0, OTP_LENGTH))
        }
        placeholder="••••••"
        placeholderTextColor="#5A5E64"
        keyboardType="number-pad"
        maxLength={OTP_LENGTH}
        autoFocus
        textContentType="oneTimeCode"
        autoComplete={Platform.OS === 'ios' ? 'one-time-code' : 'sms-otp'}
      />

      <LoadingButton
        onPress={onConfirm}
        disabled={!canSubmit}
        isLoading={otpConfirming}
        backgroundColor="#39BEB7"
        style={styles.continueButton}
      >
        Verify and continue
      </LoadingButton>

      <View style={styles.resendRow}>
        <Pressable
          onPress={canResend ? onResend : undefined}
          disabled={!canResend}
          style={({ pressed }) => [
            styles.linkButton,
            pressed && canResend && styles.linkButtonPressed,
          ]}
        >
          <Text
            style={[
              styles.linkText,
              !canResend && styles.linkTextDisabled,
            ]}
          >
            {otpRequesting
              ? 'Sending code...'
              : resendCountdown > 0
              ? `Resend in ${resendCountdown}s`
              : 'Resend code'}
          </Text>
        </Pressable>

        <Pressable
          onPress={onBack}
          style={({ pressed }) => [
            styles.linkButton,
            pressed && styles.linkButtonPressed,
          ]}
        >
          <Text style={styles.linkText}>Back</Text>
        </Pressable>
      </View>
    </View>
  );
};

const Bullet: React.FC<{ text: string }> = ({ text }) => (
  <View style={styles.bulletRow}>
    <MaterialCommunityIcons name="check-circle" size={18} color="#39BEB7" />
    <Text style={styles.bulletText}>{text}</Text>
  </View>
);

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#0B1E1D',
  },
  safeArea: {
    flex: 1,
  },
  content: {
    flex: 1,
  },
  contentContainer: {
    padding: 16,
    paddingBottom: 32,
  },
  centeredContent: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 24,
    gap: 12,
  },
  card: {
    backgroundColor: '#11302F',
    borderRadius: 16,
    padding: 20,
    borderWidth: 1,
    borderColor: 'rgba(57, 190, 183, 0.2)',
    gap: 14,
  },
  heroIcon: {
    alignSelf: 'center',
    width: 88,
    height: 88,
    borderRadius: 44,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(57, 190, 183, 0.12)',
  },
  cardTitle: {
    fontSize: 22,
    fontWeight: '700',
    color: '#FFFFFF',
    textAlign: 'center',
  },
  cardSubtitle: {
    fontSize: 14,
    lineHeight: 20,
    color: '#C7CACF',
    textAlign: 'center',
  },
  bodyText: {
    fontSize: 15,
    lineHeight: 22,
    color: '#C7CACF',
    textAlign: 'center',
  },
  successTitle: {
    fontSize: 24,
    fontWeight: '700',
    color: '#FFFFFF',
    textAlign: 'center',
    marginTop: 4,
  },
  bulletList: {
    gap: 8,
    marginTop: 4,
  },
  bulletRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  bulletText: {
    flex: 1,
    color: '#FFFFFF',
    fontSize: 14,
  },
  continueButton: {
    marginTop: 8,
    paddingVertical: 4,
  },
  primaryButton: {
    marginTop: 8,
    backgroundColor: '#39BEB7',
    borderRadius: 24,
    paddingHorizontal: 24,
    paddingVertical: 12,
  },
  primaryButtonPressed: {
    opacity: 0.8,
  },
  primaryButtonText: {
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: '700',
  },
  captchaWrap: {
    minHeight: 90,
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 4,
  },
  inlineError: {
    color: '#FF8C00',
    fontSize: 13,
    textAlign: 'center',
  },
  errorBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    backgroundColor: 'rgba(255, 107, 107, 0.18)',
    borderColor: '#FF6B6B',
    borderWidth: 1,
    borderRadius: 10,
    padding: 12,
  },
  errorBannerText: {
    color: '#FFFFFF',
    fontSize: 13,
    flex: 1,
  },
  otpInput: {
    backgroundColor: '#0B1E1D',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: 'rgba(57, 190, 183, 0.4)',
    paddingHorizontal: 16,
    paddingVertical: 14,
    color: '#FFFFFF',
    fontSize: 28,
    letterSpacing: 12,
    textAlign: 'center',
    marginTop: 6,
  },
  resendRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: 4,
  },
  linkButton: {
    paddingHorizontal: 8,
    paddingVertical: 6,
  },
  linkButtonPressed: {
    opacity: 0.6,
  },
  linkText: {
    color: '#39BEB7',
    fontSize: 14,
    fontWeight: '600',
  },
  linkTextDisabled: {
    color: '#5A5E64',
  },
  emphasis: {
    color: '#FFFFFF',
    fontWeight: '600',
  },
  stepIndicator: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 20,
    paddingHorizontal: 8,
  },
  stepItem: {
    alignItems: 'center',
    gap: 4,
  },
  stepDot: {
    width: 28,
    height: 28,
    borderRadius: 14,
    backgroundColor: '#1A1D21',
    borderWidth: 1,
    borderColor: '#3A4045',
    alignItems: 'center',
    justifyContent: 'center',
  },
  stepDotActive: {
    backgroundColor: '#39BEB7',
    borderColor: '#39BEB7',
  },
  stepDotText: {
    color: '#8D8E90',
    fontSize: 12,
    fontWeight: '700',
  },
  stepDotTextActive: {
    color: '#FFFFFF',
  },
  stepLabel: {
    fontSize: 11,
    color: '#8D8E90',
  },
  stepLabelActive: {
    color: '#FFFFFF',
    fontWeight: '600',
  },
  stepConnector: {
    flex: 1,
    height: 2,
    backgroundColor: '#3A4045',
    marginHorizontal: 6,
    marginBottom: 14,
  },
  stepConnectorActive: {
    backgroundColor: '#39BEB7',
  },
});

export default BecomeHost;
